import { useState, useCallback } from 'react';
import { appContext, stringifyPublisher } from '@provider/app-context';
import { useNotifier } from '@provider/notifier';
import { userIdentityStore } from '@provider/user-identity';
import { loadExportLibrary, slugify, asyncTimeout, downloadURI } from '@utils/common';
import { useProgress } from '../hook.progress.js';
import { resolveExportTarget, applyCustomCssStylesheet, hideIrrelevantNodes, extractExportInput, resetIrrelevantNodes } from '../util.export.js';

function useExport(input, args) {
  const [progress, setProgress, resetProgress] = useProgress();
  const [hasExported, setHasExported] = useState(false);
  const notifier = useNotifier();
  return {
    progress,
    hasExported,
    export: useCallback(async () => {
      setProgress(0);
      const gExport = window.gExport ?? (await loadExportLibrary().catch(() => {
        resetProgress();
        notifier.notify({
          id: "pdf-export.save.error",
          type: "error",
          body: "Der opstod en fejl, da vi forsøgte at generere dit dokument. Prøv igen senere."
        });
      }));
      if (!gExport) {
        return;
      }
      await args.onBeforeExport?.();
      const fileName = slugify(args.componentTitle);
      const resolvedInput = await (async () => {
        const customInput = "content" in input ? await input.content(args.format) : undefined;
        if (customInput) {
          return {
            input: customInput
          };
        } else if ("ref" in input) {
          const target = resolveExportTarget(args.format, input.ref.current);
          if (!target) {
            return undefined;
          }

          // inject custom print stylesheet if any was given
          const customPrintStylesheet = applyCustomCssStylesheet(args.css);
          if (args.hideIrrelevantDOMNodesBeforeExport) {
            hideIrrelevantNodes(target);
            await asyncTimeout(10);
          }
          return {
            input: extractExportInput(args.format, target),
            target,
            customPrintStylesheet
          };
        }
      })();
      if (!resolvedInput) {
        await args.onAfterHTMLSent?.();
        await args.onAfterExport?.();
        return;
      }
      const context = appContext.getState();
      const user = userIdentityStore.getState().user;
      const exportArgs = await (async () => {
        switch (args.format) {
          case "pdf":
            return {
              fileName,
              format: args.format,
              preset: `${args.orientation}.full`,
              input: resolvedInput.input,
              baseUrl: window.location.href
            };
          case "docx":
            return {
              fileName,
              format: args.format,
              templateData: {
                product: context.page,
                course: context.course ? {
                  title: context.course?.title,
                  authors: (await appContext.getPageAuthors())?.join(", ")
                } : undefined,
                date: new Date().toLocaleDateString("da-DK", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                }),
                publisher: stringifyPublisher(context.publisher),
                user: user ? {
                  name: user?.name,
                  institution: user?.institution?.name
                } : undefined
              },
              input: resolvedInput.input,
              baseUrl: window.location.href
            };
        }
      })();
      let hasResetPrintStyles = false;
      gExport.convert(exportArgs).progress(async evt => {
        if (evt.progress === 100) {
          resetProgress();
        } else {
          setProgress(evt.progress / 100);
        }
        if (evt.progress > 0 && !hasResetPrintStyles) {
          hasResetPrintStyles = true;
          await asyncTimeout(10);
          await args.onAfterHTMLSent?.();

          // remove custom print stylesheet as soon as possible so it doesn't
          // leak into the abstraction
          resolvedInput.customPrintStylesheet?.remove();
          if (resolvedInput.target && args.hideIrrelevantDOMNodesBeforeExport) {
            resetIrrelevantNodes(resolvedInput.target);
          }
        }
      }).then(async res => {
        resetProgress();
        setHasExported(true);
        downloadURI(res.url, `${fileName}.${args.format}`);
        await args.onAfterExport?.();
      }).catch(async err => {
        console.error(err);
        resetProgress();
        notifier.notify({
          id: "pdf-export.save.error",
          type: "error",
          body: "Der opstod en fejl, da vi forsøgte at generere dit dokument. Prøv igen senere."
        });
        if (!hasResetPrintStyles) {
          hasResetPrintStyles = true;
          await asyncTimeout(10);
          await args.onAfterHTMLSent?.();

          // remove custom print stylesheet as soon as possible so it doesn't
          // leak into the abstraction
          resolvedInput.customPrintStylesheet?.remove();
          if (resolvedInput.target && args.hideIrrelevantDOMNodesBeforeExport) {
            resetIrrelevantNodes(resolvedInput.target);
          }
        }
        await args.onAfterExport?.();
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [args.componentTitle, args.css, args.format, args.onAfterExport, args.onBeforeExport, notifier, resetProgress, setProgress, input])
  };
}

export { useExport };
//# sourceMappingURL=hook.export.js.map
